/**
 * PostGeneratorCard — the All-Access LinkedIn Post Generator (D4).
 *
 * Gated via getTierAccess(user): non-All-Access users see the card locked
 * behind an upgrade prompt (Pro users get the "included in All Access" note);
 * the API is the real enforcement point (backend require_all_access). AA users
 * enter a topic + point of view and get three distinct post variants, each
 * copy-to-clipboard.
 *
 * Paste-text only — no URL input anywhere.
 */

import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import EditNoteIcon from "@mui/icons-material/EditNote";
import LockIcon from "@mui/icons-material/Lock";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import {
  generateLinkedInPosts,
  type LinkedInPostsResult,
  type LinkedInPostTone,
  type LinkedInPostVariant,
} from "../../api/client";
import { getTierAccess } from "../../utils/tier";
import ProUpsellNote from "../ProUpsellNote";
import { track } from "../../utils/analytics";

const TONES: { value: LinkedInPostTone; labelKey: string }[] = [
  { value: "practitioner", labelKey: "PostGeneratorCard.tonePractitioner" },
  { value: "story", labelKey: "PostGeneratorCard.toneStory" },
  { value: "contrarian", labelKey: "PostGeneratorCard.toneContrarian" },
  { value: "teacher", labelKey: "PostGeneratorCard.toneTeacher" },
];

const CAPS = { topic: 200, pov: 500, audience: 120, positioning: 1000 };

export default function PostGeneratorCard({
  user,
}: {
  user: { plan_tier?: string | null; is_moderator?: boolean | null } | null | undefined;
}) {
  const theme = useTheme();
  const { t } = useTranslation("linkedin");
  const navigate = useNavigate();
  const tier = getTierAccess(user);
  const canGenerate = tier.canAccessProducts || tier.isModerator;

  const [topic, setTopic] = useState("");
  const [pov, setPov] = useState("");
  const [audience, setAudience] = useState("");
  const [tone, setTone] = useState<LinkedInPostTone>("practitioner");
  const [positioning, setPositioning] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<LinkedInPostsResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = !!topic.trim() && !!pov.trim() && !!audience.trim();

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    try {
      const data = await generateLinkedInPosts({
        topic,
        pov,
        audience,
        tone,
        positioning: positioning.trim() || null,
      });
      setResult(data);
      track("linkedin.post_generated", { tone });
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(
        (typeof detail === "string" && detail) ||
          t("PostGeneratorCard.errorFallback"),
      );
    } finally {
      setLoading(false);
    }
  };

  // --- Gated (non-All-Access) ---
  if (!canGenerate) {
    return (
      <Container maxWidth="md" disableGutters>
        {tier.isPro ? (
          <ProUpsellNote />
        ) : (
          <Paper
            elevation={0}
            sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), border: `1px dashed ${alpha(theme.palette.primary.main, 0.35)}`, borderRadius: 3, p: 4, textAlign: "center" }}
          >
            <LockIcon sx={{ fontSize: 40, color: alpha(theme.palette.primary.main, 0.5), mb: 2 }} />
            <Typography variant="h6" fontWeight={500} gutterBottom>
              {t("PostGeneratorCard.gatedTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 480, mx: "auto" }}>
              {t("PostGeneratorCard.gatedBody")}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/pricing")}
              endIcon={<ArrowForwardIcon />}
              sx={{ bgcolor: theme.palette.primary.main, fontWeight: 500, textTransform: "none", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) } }}
            >
              {t("PostGeneratorCard.seeAllAccessPricingButton")}
            </Button>
          </Paper>
        )}
      </Container>
    );
  }

  // --- Results ---
  if (result) {
    return (
      <Container maxWidth="md" disableGutters>
        <Stack spacing={3}>
          {result.variants.map((v, i) => (
            <VariantCard key={i} variant={v} index={i} />
          ))}
          <Stack direction="row">
            <Button variant="outlined" onClick={() => setResult(null)} sx={{ fontWeight: 500, textTransform: "none" }}>
              {t("PostGeneratorCard.generateAnotherButton")}
            </Button>
          </Stack>
        </Stack>
      </Container>
    );
  }

  // --- Form (All-Access) ---
  return (
    <Container maxWidth="md" disableGutters>
      <Paper
        elevation={0}
        sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: { xs: 3, md: 4 } }}
      >
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <EditNoteIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
          <Typography variant="h6" fontWeight={500}>
            {t("PostGeneratorCard.title")}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
          {t("PostGeneratorCard.intro")}
        </Typography>

        <Stack spacing={2.5}>
          <Capped label={t("PostGeneratorCard.topicLabel")} value={topic} onChange={setTopic} max={CAPS.topic} disabled={loading} placeholder={t("PostGeneratorCard.topicPlaceholder")} />
          <Capped label={t("PostGeneratorCard.povLabel")} value={pov} onChange={setPov} max={CAPS.pov} disabled={loading} multiline minRows={2} placeholder={t("PostGeneratorCard.povPlaceholder")} />
          <Capped label={t("PostGeneratorCard.audienceLabel")} value={audience} onChange={setAudience} max={CAPS.audience} disabled={loading} placeholder={t("PostGeneratorCard.audiencePlaceholder")} />
          <Box>
            <Typography variant="subtitle2" fontWeight={500} gutterBottom>
              {t("PostGeneratorCard.toneLabel")}
            </Typography>
            <TextField
              select
              fullWidth
              value={tone}
              onChange={(e) => setTone(e.target.value as LinkedInPostTone)}
              disabled={loading}
              inputProps={{ "aria-label": t("PostGeneratorCard.toneLabel") }}
            >
              {TONES.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Capped label={t("PostGeneratorCard.positioningLabel")} value={positioning} onChange={setPositioning} max={CAPS.positioning} disabled={loading} multiline minRows={2} placeholder={t("PostGeneratorCard.positioningPlaceholder")} />
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Stack direction="row" justifyContent="flex-end" mt={3}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading || !canSubmit}
            sx={{ fontWeight: 500, px: 4, textTransform: "none" }}
          >
            {loading ? t("PostGeneratorCard.generatingButton") : t("PostGeneratorCard.generateButton")}
          </Button>
        </Stack>
        {loading && (
          <LinearProgress
            sx={{ mt: 2, height: 6, borderRadius: 3, "& .MuiLinearProgress-bar": { bgcolor: theme.palette.primary.light } }}
          />
        )}
      </Paper>
    </Container>
  );
}

function Capped({
  label,
  value,
  onChange,
  max,
  disabled,
  placeholder,
  multiline,
  minRows,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  max: number;
  disabled: boolean;
  placeholder?: string;
  multiline?: boolean;
  minRows?: number;
}) {
  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={500} gutterBottom>
        {label}
      </Typography>
      <TextField
        fullWidth
        multiline={multiline}
        minRows={minRows}
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, max))}
        disabled={disabled}
        placeholder={placeholder}
        inputProps={{ maxLength: max, "aria-label": label }}
      />
      <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.5 }}>
        {value.length.toLocaleString()} / {max.toLocaleString()}
      </Typography>
    </Box>
  );
}

function VariantCard({ variant, index }: { variant: LinkedInPostVariant; index: number }) {
  const theme = useTheme();
  const { t } = useTranslation("linkedin");
  const [copied, setCopied] = useState(false);

  const fullText = `${variant.hook}\n\n${variant.body}\n\n${variant.cta}\n\n${variant.hashtags.join(" ")}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(fullText);
    setCopied(true);
    track("linkedin.post_copied", { variant: index });
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <Paper
      elevation={0}
      sx={{ border: `2px solid ${alpha(theme.palette.primary.main, 0.25)}`, borderRadius: 3, p: 3 }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Chip
          label={t("PostGeneratorCard.variantLabel", { index: index + 1 })}
          size="small"
          sx={{ fontWeight: 500, bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main }}
        />
        <Tooltip title={copied ? t("PostGeneratorCard.copiedTooltip") : t("PostGeneratorCard.copyFullPostTooltip")}>
          <IconButton
            size="small"
            aria-label={t("PostGeneratorCard.copyVariantAriaLabel", { index: index + 1 })}
            onClick={handleCopy}
            sx={{ color: copied ? theme.palette.success.dark : theme.palette.primary.main }}
          >
            {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>

      <Typography variant="h6" fontWeight={500} sx={{ lineHeight: 1.4, mb: 1.5 }}>
        {variant.hook}
      </Typography>
      <Box
        component="div"
        sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7, mb: 2, color: "text.primary" }}
      >
        {variant.body}
      </Box>
      <Typography variant="body2" fontWeight={500} sx={{ mb: 2, color: theme.palette.primary.main }}>
        {variant.cta}
      </Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" gap={0.75}>
        {variant.hashtags.map((h, i) => (
          <Chip key={i} label={h} size="small" variant="outlined" sx={{ fontSize: "0.72rem" }} />
        ))}
      </Stack>
    </Paper>
  );
}
